import PublicNav from '@/components/PublicNav';
import BackToTop from '@/components/BackToTop';
import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiCall } from '@/App';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, Eye, Layers } from 'lucide-react';
import DOMPurify from 'dompurify';
import { extractFaq, faqJsonLd } from '@/lib/blogFaq';
import { isRealEngagementGuide } from '@/lib/realEngagementGuides';
import { RealMyPackageBox } from '@/components/blog/RealMyPackageBox';
import { Breadcrumbs, breadcrumbJsonLd } from '@/components/blog/Breadcrumbs';
import { AUTHOR, authorJsonLd, PUBLISHER } from '@/lib/author';
import NotFoundPage from '@/pages/NotFoundPage';
import { staticPost, staticRelated, loadStaticContent } from '@/lib/blogStatic';

const PROSE = 'prose prose-sm max-w-none text-[#0F172A] leading-relaxed prose-headings:text-[#0F172A] prose-a:text-[#9333EA] prose-strong:text-[#0F172A]';
// Imej pertama artikel biasanya elemen LCP: beri keutamaan muat turun tinggi
const boostFirstImg = html => html.replace(/<img(?![^>]*fetchpriority)/i, '<img fetchpriority="high"');
const Prose = ({ html, first }) => <div className={PROSE} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(first ? boostFirstImg(html) : html, { ADD_ATTR: ['fetchpriority'] }) }} />;

function HtmlContent({ html, withBox }) {
  if (!withBox) return <Prose html={html} first />;
  const i = html.search(/<h2[^>]*>\s*Soalan Lazim/i);
  const [main, rest] = i > 0 ? [html.slice(0, i), html.slice(i)] : [html, ''];
  return <><Prose html={main} first /><RealMyPackageBox />{rest && <Prose html={rest} />}</>;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  // Render pertama dari data statik (H1 + meta serta-merta, tanpa API); kandungan statik & respons API menyusul
  const [post, setPost] = useState(() => staticPost(slug));
  const [related, setRelated] = useState(() => (post ? staticRelated(post) : []));
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    let fromApi = false;
    const s = staticPost(slug);
    window.scrollTo(0, 0);
    setPost(s); setRelated(s ? staticRelated(s) : []); setMissing(false);
    if (s) loadStaticContent(slug).then(content => { if (alive && !fromApi) setPost(p => ({ ...p, content })); }).catch(() => {});
    apiCall('get', `/blog/${slug}`)
      .then(d => { if (!alive) return; fromApi = true; setPost(d.post); setRelated(d.related || []); })
      .catch(() => { if (alive && !s) setMissing(true); });
    return () => { alive = false; };
  }, [slug]);

  if (missing) return <NotFoundPage />;
  if (!post) return <div className="min-h-screen bg-[#FCFAFF] flex items-center justify-center"><p className="text-[#9CA3AF]">Loading...</p></div>;

  const faqs = extractFaq(post.content);
  const firstImg = (post.content || '').match(/<img[^>]+src="([^"]+)"/)?.[1];
  const desc = post.meta_description || post.excerpt || post.title;
  const url = `https://flexaviral.com/blog/${slug}`;
  const crumbs = [{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' },
    ...(post.topic ? [{ label: post.topic.name, to: `/blog/topik/${post.topic.slug}` }] : []),
    { label: post.title, path: `/blog/${slug}` }];
  const fmt = d => new Date(d).toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#FCFAFF]">
      <Helmet>
        <title>{`${post.title} | Flexa Blog`}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        {post.created_at && <meta property="article:published_time" content={post.created_at} />}
        {post.updated_at && <meta property="article:modified_time" content={post.updated_at} />}
        <meta property="article:author" content={AUTHOR.display} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:card" content={post.cover_image ? 'summary_large_image' : 'summary'} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        {firstImg && <link rel="preload" as="image" href={firstImg} fetchpriority="high" />}
        {post.cover_image && <meta name="twitter:image" content={post.cover_image} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": desc,
          "url": url,
          ...(post.created_at ? { "datePublished": post.created_at, "dateModified": post.updated_at || post.created_at } : {}),
          "author": authorJsonLd(),
          "publisher": PUBLISHER,
          "mainEntityOfPage": { "@type": "WebPage", "@id": url },
          ...(post.topic ? { "articleSection": post.topic.name } : {}),
          "inLanguage": "ms-MY",
          "keywords": (post.tags || []).join(', '),
          ...(post.cover_image ? { "image": post.cover_image } : {})
        })}</script>
        {faqs.length >= 2 && <script type="application/ld+json">{JSON.stringify(faqJsonLd(faqs))}</script>}
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(crumbs))}</script>
      </Helmet>
      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 py-10">
        <Breadcrumbs items={crumbs} />

        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">{post.title}</h1>

        {/* Ruang tetap (min-h) supaya tiada anjakan susun atur bila tarikh/views tiba dari API */}
        <div className="text-xs text-[#9CA3AF] mb-6 space-y-1.5">
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap min-h-10 sm:min-h-4">
            {post.created_at && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Diterbitkan {fmt(post.created_at)}</span>}
            {post.updated_at && post.updated_at.slice(0, 10) !== String(post.created_at).slice(0, 10) && (
              <span className="text-emerald-600" data-testid="blog-post-updated">Dikemaskini {fmt(post.updated_at)}</span>
            )}
            {post.views != null && <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{post.views || 0} views</span>}
          </div>
          <div>Oleh <Link to={`/penulis/${AUTHOR.slug}`} className="font-semibold text-[#0F172A] hover:text-[#9333EA]" data-testid="blog-post-author">{AUTHOR.display}</Link></div>
        </div>

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="blog-post-tags">
            {post.tags.map(t => <span key={t} className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap bg-[#F8F5FF] text-[#9333EA] border border-[#E9D5FF]">{t}</span>)}
          </div>
        )}

        <div className="bg-white border border-[#E9D5FF] rounded-xl p-6 md:p-8">
          {/* Render HTML content or plain text */}
          {post.content == null ? (
            <div className="space-y-3 animate-pulse min-h-[150vh]" data-testid="blog-content-skeleton">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-4 bg-zinc-100 rounded" style={{ width: `${100 - i * 7}%` }} />)}</div>
          ) : post.content.includes('<') ? (
            <HtmlContent html={post.content} withBox={isRealEngagementGuide(slug)} />
          ) : (
            <div className="prose prose-sm max-w-none text-[#0F172A] leading-relaxed" style={{ whiteSpace: 'pre-wrap' }}>
              {(post.content || '').split('\n').map((line, i) => {
                if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-[#0F172A] mt-6 mb-2">{line.replace('## ', '')}</h2>;
                if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold text-[#0F172A]">{line.replace(/\*\*/g, '')}</p>;
                if (line.startsWith('- ')) return <li key={i} className="text-sm text-[#6B7280] ml-4 list-disc">{line.replace('- ', '')}</li>;
                if (line.trim() === '') return <br key={i} />;
                return <p key={i} className="text-sm text-[#6B7280] mb-2">{line}</p>;
              })}
            </div>
          )}
        </div>

        {post.topic && (
          <Link to={`/blog/topik/${post.topic.slug}`} data-testid="blog-post-pillar-link"
            className="mt-8 flex items-center justify-between gap-3 bg-[#F8F5FF] border border-[#E9D5FF] rounded-xl p-4 hover:border-[#9333EA] transition-colors group">
            <span className="flex items-center gap-2 text-sm text-[#0F172A]"><Layers className="h-4 w-4 text-[#9333EA]" />Semua panduan dalam topik <strong>{post.topic.name}</strong></span>
            <ArrowRight className="h-4 w-4 text-[#9333EA] transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}

        {/* Author Bio */}
        <div className="mt-8 bg-white border border-[#E9D5FF] rounded-xl p-5 flex items-start gap-4" data-testid="blog-author-box">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#9333EA] to-[#7E22CE] flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg">{AUTHOR.initials}</span>
          </div>
          <div>
            <Link to={`/penulis/${AUTHOR.slug}`} className="text-sm font-bold text-[#0F172A] hover:text-[#9333EA]" data-testid="blog-author-box-name">{AUTHOR.display}</Link>
            <p className="text-xs text-[#9CA3AF] leading-relaxed mt-1">{AUTHOR.bio} <Link to="/polisi-editorial" className="text-[#9333EA] hover:underline">Polisi editorial</Link></p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 bg-gradient-to-br from-[#9333EA] to-[#7E22CE] rounded-xl p-6 text-center text-white">
          <h3 className="text-lg font-bold mb-2">Nak naikkan sosial media anda?</h3>
          <p className="text-purple-200 text-sm mb-4">Daftar Flexa sekarang, percuma!</p>
          <Link to="/signup" className="inline-flex items-center gap-2 bg-white text-[#9333EA] px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-purple-50 transition-colors">Daftar Sekarang</Link>
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <div className="mt-8" data-testid="related-articles">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">Artikel Berkaitan</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map(r => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="bg-white border border-[#E9D5FF] rounded-xl p-4 hover:border-[#9333EA] transition-colors group">
                  <p className="text-sm font-semibold text-[#0F172A] group-hover:text-[#9333EA] line-clamp-2 mb-1">{r.title}</p>
                  <p className="text-xs text-[#9CA3AF] line-clamp-2">{r.excerpt || r.meta_description}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {post.related_service_url && (
          <div className="mt-8 bg-white border border-[#E9D5FF] rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="blog-related-service">
            <p className="text-sm text-[#0F172A]">Servis berkaitan: <Link to={post.related_service_url} className="font-semibold text-[#9333EA] hover:underline">{post.related_service_label}</Link></p>
            <Link to="/services" className="text-xs text-zinc-500 hover:text-violet-600">Semua Servis</Link>
          </div>
        )}
      </div>
    </div>
  );
}
